/**
 * SupplierPerformancePage — /dashboard/supplier/performance
 *
 * The "how am I doing" screen. KPI cards, 0-100 score, benchmark line,
 * 7-day trend bars, positive signals, improvement actions, visibility
 * state. Every insight is tied to a behaviour — not a number.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Lightning, Fire, Trophy, TrendUp, TrendDown,
  CheckCircle, Warning, ArrowUpRight,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIVE_COPY = {
  fast_replies: { Icon: Lightning, text: 'Fast replies' },
  strong_booking_rate: { Icon: Trophy, text: 'Strong booking rate' },
  active_this_week: { Icon: Fire, text: 'Active this week' },
};

const IMPROVEMENT_COPY = {
  reply_faster: { title: 'Reply faster to new enquiries', hint: 'Suppliers who reply within 10 minutes get more bookings.' },
  send_bookings_earlier: { title: 'Send booking requests sooner', hint: 'Customers convert better when you push to confirm early.' },
  complete_profile: { title: 'Complete your profile', hint: 'Add photos + a clear description to get more clicks.' },
  stay_active: { title: 'Stay active this week', hint: 'Active suppliers rank higher and get surfaced in search.' },
};

const VISIBILITY_COPY = {
  high: { label: 'HIGH', emoji: '🔥', tint: 'from-emerald-400/20 to-[#C29D52]/10 border-emerald-400/30', text: "You're appearing in more searches than most suppliers." },
  good: { label: 'GOOD', emoji: '⚡', tint: 'from-[#C29D52]/20 to-[#B89762]/10 border-[#B89762]/40/30', text: "You're being surfaced to customers — keep it up." },
  needs_improvement: { label: 'NEEDS IMPROVEMENT', emoji: '⚠️', tint: 'from-amber-400/20 to-amber-300/5 border-amber-400/30', text: "Improve your ranking to appear in more searches." },
};

const SupplierPerformancePage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [window, setWindow] = useState('7d');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    try {
      const r = await axios.get(`${API}/suppliers/me/performance`, { params: { window: w } });
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate('/login');
      else toast.error(err?.response?.data?.detail || 'Could not load performance');
    }
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    load(window).finally(() => setLoading(false));
  }, [window, load]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center">
      <p className="text-white/40 text-sm">Loading…</p>
    </div>
  );
  if (!data) return null;

  const { kpis, score, percentile, visibility_level, trend, positive_signals, improvement_actions } = data;
  const vis = VISIBILITY_COPY[visibility_level] || VISIBILITY_COPY.good;

  const maxTrend = Math.max(1, ...trend.leads, ...trend.bookings);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="performance-page">
      <div className="max-w-3xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate('/dashboard/supplier')}
          className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </button>

        <div className="flex items-end justify-between mb-5">
          <div>
            <h1 className="text-3xl font-black">Performance</h1>
            <p className="text-white/40 text-xs mt-1">Last {window === '30d' ? '30' : '7'} days</p>
          </div>
          <div className="flex gap-1 rounded-full bg-white/5 border border-white/10 p-1 text-[11px] font-bold">
            {['7d', '30d'].map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={`px-3 py-1 rounded-full transition ${window === w ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                data-testid={`window-${w}`}
              >
                {w === '7d' ? '7 days' : '30 days'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="kpi-cards">
          <KpiCard label="Leads" value={kpis.leads} testId="kpi-leads" />
          <KpiCard label="Replies" value={kpis.replied} sub={kpis.leads > 0 ? `${Math.round(kpis.reply_rate * 100)}%` : '—'} accent="cyan" testId="kpi-replies" />
          <KpiCard label="Bookings" value={kpis.bookings} sub={kpis.leads > 0 ? `${Math.round(kpis.booking_rate * 100)}%` : '—'} accent="emerald" testId="kpi-bookings" />
        </div>

        {/* Score + benchmark card */}
        <div
          className="rounded-2xl p-5 mb-4 border"
          style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.2), rgba(194,157,82,0.10))', borderColor: 'rgba(255,255,255,0.1)' }}
          data-testid="score-card"
        >
          <p className="text-[10px] font-black uppercase tracking-[2px] text-white/50 mb-1">Performance Score</p>
          <div className="flex items-end gap-2">
            <p className="text-5xl font-black tabular-nums" data-testid="score-value">{score}</p>
            <p className="text-white/40 text-sm font-bold mb-2">/ 100</p>
          </div>
          {percentile !== null && percentile !== undefined && (
            <p className="text-white/70 text-sm mt-2" data-testid="percentile-line">
              Performing better than <span className="text-[#C29D52] font-bold">{percentile}%</span> of suppliers
            </p>
          )}
          {kpis.avg_reply_seconds !== null && (
            <p className="text-white/40 text-xs mt-1 flex items-center gap-1">
              <Lightning size={11} weight="fill" /> Avg reply time: {Math.max(1, Math.round(kpis.avg_reply_seconds / 60))} min{Math.round(kpis.avg_reply_seconds / 60) === 1 ? '' : 's'}
            </p>
          )}
        </div>

        {/* Visibility panel */}
        <div
          className={`rounded-2xl p-5 mb-4 border bg-gradient-to-br ${vis.tint}`}
          data-testid="visibility-panel"
        >
          <p className="text-[10px] font-black uppercase tracking-[2px] text-white/60 mb-1">Your visibility</p>
          <p className="text-xl font-black mb-1">{vis.emoji} {vis.label}</p>
          <p className="text-white/70 text-xs">{vis.text}</p>
        </div>

        {/* 7-day trends */}
        <div className="rounded-2xl p-5 mb-4 bg-white/[0.03] border border-white/10" data-testid="trends-card">
          <p className="text-[10px] font-black uppercase tracking-[2px] text-white/50 mb-3">Last 7 days</p>
          <TrendBars label="Leads" color="#C29D52" values={trend.leads} max={maxTrend} />
          <div className="mt-4">
            <TrendBars label="Bookings" color="#34d399" values={trend.bookings} max={maxTrend} />
          </div>
        </div>

        {/* Positive signals */}
        {positive_signals.length > 0 && (
          <div className="rounded-2xl p-5 mb-4 bg-emerald-400/5 border border-emerald-400/20" data-testid="positive-signals">
            <p className="text-[10px] font-black uppercase tracking-[2px] text-emerald-300 mb-3 flex items-center gap-1">
              <CheckCircle size={11} weight="fill" /> What's boosting your visibility
            </p>
            <ul className="space-y-2">
              {positive_signals.map((s) => {
                const c = POSITIVE_COPY[s];
                if (!c) return null;
                const I = c.Icon;
                return (
                  <li key={s} className="flex items-center gap-2 text-sm">
                    <I size={13} weight="fill" className="text-emerald-300 shrink-0" />
                    <span>{c.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Improvement actions */}
        {improvement_actions.length > 0 && (
          <div className="rounded-2xl p-5 mb-4 bg-amber-400/5 border border-amber-400/20" data-testid="improvement-actions">
            <p className="text-[10px] font-black uppercase tracking-[2px] text-amber-300 mb-3 flex items-center gap-1">
              <Warning size={11} weight="fill" /> Improve your ranking
            </p>
            <ul className="space-y-3">
              {improvement_actions.map((s) => {
                const c = IMPROVEMENT_COPY[s];
                if (!c) return null;
                return (
                  <li key={s}>
                    <p className="text-sm font-bold">{c.title}</p>
                    <p className="text-white/50 text-xs mt-0.5">{c.hint}</p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Cause-effect line */}
        <p className="text-white/40 text-[11px] text-center my-3" data-testid="cause-effect">
          Reply within 10 minutes → rank higher in search · More bookings → appear higher to customers
        </p>

        {/* Upgrade nudge — high demand path */}
        {!data.is_pro && kpis.leads >= 2 && (
          <div
            className="rounded-2xl p-5 mt-4 mb-4 border"
            style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.15), rgba(194,157,82,0.08))', borderColor: 'rgba(194,157,82,0.25)' }}
            data-testid="perf-upgrade-nudge"
          >
            <p className="font-bold text-sm mb-1">You're getting strong demand 🔥</p>
            <p className="text-white/60 text-xs mb-3">
              Upgrade to Studio Pass to handle more leads, send bookings, and grow faster.
            </p>
            <button
              onClick={() => navigate('/studio-pass')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="perf-upgrade-btn"
            >
              Upgrade <ArrowUpRight size={12} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, accent, testId }) => {
  const accentCls = accent === 'cyan' ? 'text-[#C29D52]' : accent === 'emerald' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="rounded-xl p-4 bg-white/[0.03] border border-white/10" data-testid={testId}>
      <p className="text-[10px] font-black uppercase tracking-wider text-white/40 mb-1">{label}</p>
      <p className={`text-3xl font-black tabular-nums ${accentCls}`}>{value}</p>
      {sub && <p className="text-white/50 text-[11px] mt-1">{sub}</p>}
    </div>
  );
};

const TrendBars = ({ label, color, values, max }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-xs font-bold text-white/60">{label}</p>
      <p className="text-[10px] text-white/40">Total: {values.reduce((a, b) => a + b, 0)}</p>
    </div>
    <div className="flex items-end gap-1 h-16">
      {values.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end">
          <div
            className="w-full rounded-t"
            style={{ height: `${Math.max(4, (v / max) * 100)}%`, background: color, opacity: v === 0 ? 0.15 : 0.9 }}
          />
        </div>
      ))}
    </div>
    <div className="flex justify-between mt-1">
      {['6d', '5d', '4d', '3d', '2d', 'yda', 'today'].map((d, i) => (
        <span key={i} className="text-[9px] text-white/30 flex-1 text-center">{d}</span>
      ))}
    </div>
  </div>
);

export default SupplierPerformancePage;
